import {flavorDescriptions, colors, strokeWidth} from '../constants';

import {
  createPoint,
  buildGeometryInstance,
  isSameCoordinatePair,
  getLayerForFlavoredEntityType,
  handleAddCorrectnessIcon,
  hasCoords
} from './entities.utils';

const editOptions = [
  {
    label: 'Reverse direction',
    apply: ({board, instance}) => {
      const {onClick} = instance;
      const updatedEntity = {
        ...instance.entity,
        elements: {
          points: [...instance.entity.elements.points].reverse()
        }
      };
      instance.remove();
      return ray.create({
        board,
        entity: updatedEntity,
        onClick
      });
    }
  }
];

const ray = {
  /**
   * @example
   * const entity = {
   *   type: 'ray',
   *   elements: {
   *     points: [
   *       [0, 0],
   *       [4, 1]
   *     ]
   *   },
   *   flavor: 'studentGuess'
   * };
   */

  create({board, entity, onClick}) {
    const {
      elements: {points},
      flavor
    } = entity;

    const rayPoints = points.map((coords) => createPoint({board, coords, flavor}));

    const {color} = flavorDescriptions[flavor];

    const ray = board.create('line', rayPoints, {
      strokeWidth,
      highlightStrokeWidth: strokeWidth,
      straightFirst: false,
      lastArrow: true,
      strokeColor: color,
      highlightStrokeColor: color,
      touchFirstPoint: true,
      touchLastPoint: true,
      layer: getLayerForFlavoredEntityType('line', flavor),
      tabindex: null
    });

    const correctnessIcon = handleAddCorrectnessIcon({board, flavor, coords: points[0]});

    return buildGeometryInstance({
      board,
      entity,
      geometryElement: ray,
      constituentElements: [...rayPoints, ray],
      onClick,
      editOptions,
      correctnessIcon
    });
  },
  createPartial({board, partialInstance, coords, flavor}) {
    if (!partialInstance) {
      return {
        entity: {
          type: 'ray',
          elements: {
            points: []
          },
          flavor
        },
        geometryElements: [],
        done: false,
        remove() {
          return null;
        }
      };
    }

    const isValid = !hasCoords(partialInstance.entity.elements, coords);

    if (!isValid) return null;

    const points = [...partialInstance.entity.elements.points, coords];
    const nextPoint = createPoint({board, coords, flavor});
    const geometryElements = [...partialInstance.geometryElements, nextPoint];
    return {
      entity: {
        type: 'ray',
        elements: {
          points
        },
        flavor
      },
      geometryElements,
      done: points.length === 2,
      instructions: 'Complete the ray by placing a second point.',
      handleCursorMove(cursorCoords) {
        if (board.elementsByName.previewRay) {
          board.removeAncestors(board.elementsByName.previewRay);
        }

        if (!coords || !cursorCoords || isSameCoordinatePair(coords, cursorCoords)) {
          return;
        }

        board.create('line', [coords, cursorCoords], {
          name: 'previewRay',
          strokeWidth,
          highlightStrokeWidth: strokeWidth,
          strokeColor: colors.preview,
          highlightStrokeColor: colors.preview,
          dash: 3,
          straightFirst: false,
          lastArrow: true,
          touchFirstPoint: true,
          touchLastPoint: true,
          tabindex: null
        });
      },
      remove() {
        geometryElements.forEach((element) => board.removeAncestors(element));
        return null;
      }
    };
  }
};

export default ray;
